import { API } from "@/lib/api";
import { subjectColor } from "@/lib/constants";

// A4 optik cevap kâğıdı — baskıya hazır, derslere özel renkli
export default function OpticalSheet({ data }) {
  const { form, student, exam, institution_name, logo_url } = data;
  const subjects = exam.subjects;
  const options = exam.options || ["A", "B", "C", "D"];
  const qrUrl = `${API}/optical-forms/${form.id}/qr.png`;

  // Her ders kendi içinde 1'den numaralanır (OMR global anahtarı korunur).
  const cols = subjects.map((s) => ({ ...s }));
  const isDeneme = exam.exam_type === "deneme";
  const SAYISAL = ["matematik", "fen"];
  const sozelCols = cols.filter((s) => !SAYISAL.includes(s.key));
  const sayisalCols = cols.filter((s) => SAYISAL.includes(s.key));

  const Bubble = ({ label }) => <span className="bubble">{label}</span>;

  const renderGrid = (list) => (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${list.length}, minmax(0,1fr))` }}>
      {list.map((s) => {
        const c = subjectColor(s.key);
        return (
          <div key={s.key} className="rounded-md overflow-hidden border" style={{ borderColor: c.hex }}>
            <div className="text-white text-[8.5px] font-bold text-center py-1.5" style={{ background: c.hex }}>
              {s.name.toUpperCase()}
            </div>
            <div className="p-[3px]" style={{ background: c.hex + "12" }}>
              {Array.from({ length: s.question_count }, (_, i) => i + 1).map((n) => (
                <div key={n} className="flex items-center gap-1 bg-white rounded px-[2px] mb-[2px]">
                  <span className="w-5 text-right text-[8.5px] font-bold" style={{ color: c.hex }}>{n}</span>
                  <div className="flex gap-[2.5px] py-[2.5px]">
                    {options.map((o) => <Bubble key={o} label={o} />)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="optical-sheet shadow-xl mx-auto">
      {/* Hizalama işaretleri */}
      <div className="flex justify-between mb-2">
        {Array.from({ length: 12 }).map((_, i) => <span key={i} className="timing-mark" />)}
      </div>

      {/* Başlık kartı */}
      <div className="rounded-lg overflow-hidden border border-slate-300">
        <div className="bg-slate-900 text-white px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {logo_url ? <img src={logo_url} alt="logo" className="h-9 w-9 object-contain rounded" crossOrigin="anonymous" /> : null}
            <div>
              <div className="text-[10px] tracking-wider text-slate-300">{institution_name?.toUpperCase()}</div>
              <div className="text-[14px] font-bold">{exam.exam_type === "deneme" ? "DENEME SINAVI CEVAP KÂĞIDI" : "KONU TARAMA TESTİ (KTT) CEVAP KÂĞIDI"}</div>
            </div>
          </div>
          <div className="text-right text-[10px]">
            <div className="font-bold text-[11px]">{exam.name}</div>
            <div className="text-slate-300">{exam.exam_no ? `Sınav No: ${exam.exam_no} · ` : ""}{exam.date || ""}</div>
          </div>
        </div>
        <div className="flex p-3 gap-4 bg-white items-center">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[10.5px] flex-1">
            <div><span className="text-slate-400">Ad Soyad</span><br /><b className="text-[11.5px]">{student?.first_name} {student?.last_name}</b></div>
            <div><span className="text-slate-400">Okul No</span><br /><b className="text-[11.5px]">{student?.number}</b></div>
            <div><span className="text-slate-400">Sınıf / Şube</span><br /><b className="text-[11.5px]">{student?.grade}-{student?.branch}</b></div>
            <div><span className="text-slate-400">T.C. Kimlik No</span><br /><b className="text-[11.5px]">{student?.tc_no_masked}</b></div>
          </div>
          <div className="text-center shrink-0 border border-slate-200 rounded-md p-1.5">
            <img src={qrUrl} alt="QR" className="h-[96px] w-[96px]" crossOrigin="anonymous" />
            <div className="text-[7.5px] text-slate-500 mt-0.5 tracking-wider">OPTİK KİMLİK</div>
          </div>
        </div>
        <div className="text-[8.5px] px-3 py-1.5 bg-slate-50 text-slate-600 border-t border-slate-200 leading-snug">
          1. Bu formu siyah kurşun kalemle doldurunuz. 2. Cevaplarınızı baloncuğun dışına taşırmadan koyulaştırınız. 3. Değiştirmek istediğiniz cevabı iz bırakmadan siliniz.
        </div>
      </div>

      {/* Kitapçık türü satırı (yalnız Deneme) */}
      {exam.exam_type === "deneme" && (
        <div className="flex items-center gap-1.5 mt-2 px-1" data-testid="sheet-booklet-row">
          <span className="text-[10px] font-bold text-slate-900 mr-1">KİTAPÇIK TÜRÜ:</span>
          {(exam.booklets || ["A", "B", "C", "D"]).map((b) => <Bubble key={b} label={b} />)}
        </div>
      )}

      {/* Cevap alanı — ders ders renkli; Deneme'de SÖZEL / SAYISAL gruplu */}
      {isDeneme && sozelCols.length > 0 && sayisalCols.length > 0 ? (
        <>
          <div className="mt-3 mb-1 text-[9px] font-bold text-slate-900 text-center border border-slate-900 rounded bg-slate-100 py-0.5" data-testid="sheet-group-sozel">SÖZEL BÖLÜM</div>
          {renderGrid(sozelCols)}
          <div className="mt-2.5 mb-1 text-[9px] font-bold text-slate-900 text-center border border-slate-900 rounded bg-slate-100 py-0.5" data-testid="sheet-group-sayisal">SAYISAL BÖLÜM</div>
          {renderGrid(sayisalCols)}
        </>
      ) : (
        <div className="mt-3">{renderGrid(cols)}</div>
      )}

      {/* Alt hizalama işaretleri */}
      <div className="flex justify-between mt-3">
        {Array.from({ length: 12 }).map((_, i) => <span key={i} className="timing-mark" />)}
      </div>
    </div>
  );
}
